/**
 * Wire the Clawnify MCP server into a project-scoped `.mcp.json` so
 * Claude Code (and other clients that read project MCP config) pick it
 * up automatically — no hand-running `claude mcp add`.
 *
 * The server entry runs the CLI's bundled local MCP server
 * (`clawnify --mcp`), which exposes deploy / list_apps / get_app /
 * delete_app. Merge is idempotent: an existing `.mcp.json` is preserved,
 * only the `clawnify` server is added or refreshed.
 */

import { existsSync, readFileSync, unlinkSync, writeFileSync } from "node:fs";
import { join } from "node:path";

const FILE = ".mcp.json";

const SERVER = {
  command: "npx",
  args: ["clawnify", "--mcp"],
};

export type McpStatus =
  | { status: "installed"; file: string }
  | { status: "updated"; file: string }
  | { status: "skipped"; file: string; reason: string };

interface McpConfig {
  mcpServers?: Record<string, unknown>;
  [key: string]: unknown;
}

export function writeMcpConfig(cwd: string): McpStatus {
  const filePath = join(cwd, FILE);

  let config: McpConfig = {};
  let existed = false;
  if (existsSync(filePath)) {
    existed = true;
    try {
      config = JSON.parse(readFileSync(filePath, "utf8")) as McpConfig;
    } catch {
      return { status: "skipped", file: filePath, reason: "existing .mcp.json is not valid JSON" };
    }
  }

  config.mcpServers ??= {};
  const current = config.mcpServers["clawnify"];
  if (current && JSON.stringify(current) === JSON.stringify(SERVER)) {
    return { status: "skipped", file: filePath, reason: "already configured" };
  }

  const wasPresent = current !== undefined;
  config.mcpServers["clawnify"] = SERVER;
  writeFileSync(filePath, JSON.stringify(config, null, 2) + "\n");
  return existed && wasPresent
    ? { status: "updated", file: filePath }
    : { status: "installed", file: filePath };
}

/** Remove the clawnify server from `.mcp.json`. Returns true if removed. */
export function removeMcpConfig(cwd: string): boolean {
  const filePath = join(cwd, FILE);
  if (!existsSync(filePath)) return false;
  let config: McpConfig;
  try {
    config = JSON.parse(readFileSync(filePath, "utf8")) as McpConfig;
  } catch {
    return false;
  }
  if (!config.mcpServers || !(("clawnify") in config.mcpServers)) return false;
  delete config.mcpServers["clawnify"];
  // Drop an empty mcpServers object so we don't leave clutter behind.
  if (Object.keys(config.mcpServers).length === 0) delete config.mcpServers;
  // If nothing else is left, our entry was the file's only content — remove it
  // rather than leaving a meaningless `{}`.
  if (Object.keys(config).length === 0) {
    unlinkSync(filePath);
  } else {
    writeFileSync(filePath, JSON.stringify(config, null, 2) + "\n");
  }
  return true;
}
